import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Referral, ReferralFilters, ReferralStatus, ReferralPriority } from '../types/referral'
import { referralService } from '../services/referralService'
import { formatDate } from '../utils/locale'
import './ReferralList.css'

interface ReferralListProps {
  patientId?: string
  referringProviderId?: string
  onReferralClick?: (referral: Referral) => void
}

export function ReferralList({
  patientId,
  referringProviderId,
  onReferralClick,
}: ReferralListProps) {
  const [filters] = useState<ReferralFilters>({
    patientId,
    referringProviderId,
  })
  const [statusFilter, setStatusFilter] = useState<ReferralStatus | 'all'>('all')
  const [priorityFilter, setPriorityFilter] = useState<ReferralPriority | 'all'>('all')
  const [searchTerm, setSearchTerm] = useState('')

  const { data: referrals, isLoading, error } = useQuery({
    queryKey: ['referrals', filters],
    queryFn: () => referralService.getAll(filters),
  })

  // Apply client-side filters
  const filteredReferrals = referrals?.filter((r) => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false
    if (priorityFilter !== 'all' && r.priority !== priorityFilter) return false
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      return (
        r.reason.toLowerCase().includes(search) ||
        r.referredToProviderName.toLowerCase().includes(search) ||
        r.providerDetails?.specialty?.toLowerCase().includes(search) ||
        r.patientId.toLowerCase().includes(search)
      )
    }
    return true
  })

  const getStatusBadgeClass = (status: ReferralStatus) => {
    switch (status) {
      case 'draft':
        return 'status-draft'
      case 'sent':
        return 'status-sent'
      case 'acknowledged':
        return 'status-acknowledged'
      case 'completed':
        return 'status-completed'
      case 'cancelled':
        return 'status-cancelled'
      default:
        return ''
    }
  }

  const getPriorityBadgeClass = (priority: ReferralPriority) => {
    switch (priority) {
      case 'routine':
        return 'priority-routine'
      case 'urgent':
        return 'priority-urgent'
      case 'emergency':
        return 'priority-emergency'
      default:
        return ''
    }
  }

  if (isLoading) {
    return <div className="referral-list-loading">Loading referrals...</div>
  }

  if (error) {
    return <div className="referral-list-error">Error loading referrals</div>
  }

  return (
    <div className="referral-list">

      <div className="referral-list-filters">
        <div className="filter-group">
          <label htmlFor="statusFilter">Status</label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ReferralStatus | 'all')}
          >
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="priorityFilter">Priority</label>
          <select
            id="priorityFilter"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as ReferralPriority | 'all')}
          >
            <option value="all">All</option>
            <option value="routine">Routine</option>
            <option value="urgent">Urgent</option>
            <option value="emergency">Emergency</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="searchTerm">Search</label>
          <input
            id="searchTerm"
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search referrals..."
          />
        </div>
      </div>

      {filteredReferrals && filteredReferrals.length > 0 ? (
        <div className="referral-list-table">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Patient</th>
                <th>Referred To</th>
                <th>Specialty</th>
                <th>Reason</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Method</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredReferrals.map((referral) => (
                <tr
                  key={referral.id}
                  onClick={() => onReferralClick?.(referral)}
                  className="referral-row"
                >
                  <td>{formatDate(referral.referralDate)}</td>
                  <td>{referral.patientId}</td>
                  <td>{referral.referredToProviderName}</td>
                  <td>{referral.providerDetails?.specialty || '-'}</td>
                  <td>{referral.reason}</td>
                  <td>
                    <span className={`priority-badge ${getPriorityBadgeClass(referral.priority)}`}>
                      {referral.priority}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(referral.status)}`}>
                      {referral.status}
                    </span>
                  </td>
                  <td>{referral.communicationMethod}</td>
                  <td>
                    <button
                      className="btn-link"
                      onClick={(e) => {
                        e.stopPropagation()
                        onReferralClick?.(referral)
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="referral-list-empty">
          <p>No referrals found</p>
        </div>
      )}
    </div>
  )
}
